/**
 * `<ElectronicSignature>` — PD-1897: the read-only signature that closes a
 * signed Progress Note. Name and credentials come from the signer's profile;
 * the time is the signature's own, never the visit date.
 */
import { Box, Group, Text } from '@mantine/core'
import { useResource } from '@medplum/react'
import { IconLock } from '@tabler/icons-react'
import { formatSignatureTime } from '@/utils'
import { getPractitionerDisplayName } from '@/utils/providerUtils'
import type { NoteSignature } from '@/utils/provenance'
import { useTocAnchor } from './tocRegistry'

export function ElectronicSignature({ signature }: { signature: NoteSignature }) {
  useTocAnchor('signature')
  const signer = useResource(signature.signer)
  const signerName = signer ? getPractitionerDisplayName(signer) : (signature.signer.display ?? '—')

  return (
    <Box
      id="signature"
      data-testid="electronic-signature"
      mb="xl"
      style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '12px 14px',
      }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <IconLock size={16} color="#6b7280" style={{ flexShrink: 0, marginTop: 2 }} />
        <Box>
          <Text size="sm" fw={600} c="dark.6">
            Electronic Signature
          </Text>
          <Text size="sm" c="dark.7">
            Electronically signed by{' '}
            <Text span fw={700}>
              {signerName}
            </Text>{' '}
            {formatSignatureTime(signature.signedAt)}.
          </Text>
        </Box>
      </Group>
    </Box>
  )
}
